// Terrain.
//
// The ground is one fill, not one draw per cell. Both materials are baked into
// a mirrored 2x2 patch first, so the browser's own `repeat` pattern is
// seamless whatever the source looked like — which means the generator never
// has to produce a tiling texture, and there is no grid of seams to hide.
//
// Everything on top of the ground is code-drawn: a tree is a trunk and a
// canopy, a wall is a slab with a lit top edge. The whole field is meant to
// read as "an outdoors", not as a place with a history.

import { TILE } from "../../sim/constants";
import { PALETTE } from "../../content/palette";
import {
  tileAt,
  TILE_DOOR_IN,
  TILE_DOOR_OUT,
  TILE_PATH,
  TILE_ROCK,
  TILE_TREE,
  TILE_WALL,
  type Tile,
  type WorldMap,
} from "../../sim/map";

const patterns = new Map<string, CanvasPattern | null>();

/** A mirrored 2x2 copy of a material, which tiles seamlessly by construction:
 *  every edge of the patch is a mirror of the edge it meets. */
function mirrorPatch(source: CanvasImageSource, size: number): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = size * 2;
  canvas.height = size * 2;
  const ctx = canvas.getContext("2d");
  if (!ctx) return canvas;
  for (const [sx, sy] of [
    [1, 1],
    [-1, 1],
    [1, -1],
    [-1, -1],
  ] as const) {
    ctx.save();
    ctx.translate(sx > 0 ? 0 : size * 2, sy > 0 ? 0 : size * 2);
    ctx.scale(sx, sy);
    ctx.drawImage(source, 0, 0, size, size);
    ctx.restore();
  }
  return canvas;
}

export function groundPattern(ctx: CanvasRenderingContext2D, id: string, source: CanvasImageSource, size: number): CanvasPattern | null {
  const cached = patterns.get(id);
  if (cached !== undefined) return cached;
  const pattern = ctx.createPattern(mirrorPatch(source, size), "repeat");
  patterns.set(id, pattern);
  return pattern;
}

/** Deterministic per-tile jitter, so scattered trees are not all identical
 *  without anything having to store a seed per cell. */
function tileNoise(tx: number, ty: number): number {
  const h = Math.imul(tx * 0x1f1f1f1f + ty, 0x2545f491) >>> 0;
  return (h % 1000) / 1000;
}

function drawTree(ctx: CanvasRenderingContext2D, px: number, py: number, seed: number): void {
  const cx = px + TILE / 2;
  ctx.fillStyle = PALETTE.treeTrunk;
  ctx.fillRect(Math.round(cx - 1.5), py + TILE - 6, 3, 6);
  const r = 6.5 + seed * 1.5;
  ctx.fillStyle = PALETTE.tree;
  ctx.beginPath();
  ctx.arc(cx, py + TILE / 2 - 1, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = PALETTE.treeTop;
  ctx.beginPath();
  ctx.arc(cx - 1.5, py + TILE / 2 - 3, r * 0.55, 0, Math.PI * 2);
  ctx.fill();
}

function drawWall(ctx: CanvasRenderingContext2D, map: WorldMap, tx: number, ty: number, px: number, py: number): void {
  ctx.fillStyle = PALETTE.wall;
  ctx.fillRect(px, py, TILE, TILE);
  // Only a slab with open air above it gets the lit top edge, so a block of
  // wall reads as one building rather than as a stack of bricks.
  if (tileAt(map, tx, ty - 1) !== TILE_WALL) {
    ctx.fillStyle = PALETTE.wallTop;
    ctx.fillRect(px, py, TILE, 4);
  }
}

export function drawTile(ctx: CanvasRenderingContext2D, map: WorldMap, tx: number, ty: number, px: number, py: number): void {
  const tile: Tile = tileAt(map, tx, ty);
  switch (tile) {
    case TILE_PATH:
      ctx.fillStyle = PALETTE.path;
      ctx.fillRect(px, py, TILE, TILE);
      ctx.fillStyle = PALETTE.pathEdge;
      ctx.fillRect(px, py, TILE, 1);
      break;
    case TILE_TREE:
      drawTree(ctx, px, py, tileNoise(tx, ty));
      break;
    case TILE_WALL:
      drawWall(ctx, map, tx, ty, px, py);
      break;
    case TILE_ROCK:
      ctx.fillStyle = PALETTE.rock;
      ctx.fillRect(px, py, TILE, TILE);
      break;
    case TILE_DOOR_IN:
    case TILE_DOOR_OUT:
      ctx.fillStyle = PALETTE.door;
      ctx.fillRect(px + 2, py, TILE - 4, TILE);
      ctx.fillStyle = PALETTE.wallTop;
      ctx.fillRect(px + 2, py, TILE - 4, 2);
      break;
    default:
      // Grass and floor are the ground fill; there is nothing on top of them.
      break;
  }
}
